import { useState } from 'react';
import type { FunctionComponent, ReactElement, ReactNode, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import Button from '../Button';
import { defaultThemeProp } from '../../theme';
import Icon, { registerIcon } from '../Icon';
import * as caretDownIcon from '../Icon/icons/caret-down.icon';
import ExpandCollapse from '../ExpandCollapse';

import Card from './Card';
import type { CardProps } from './Card';
import CardHeader from './CardHeader';
import CardMedia from './CardMedia';
import CardContent from './CardContent';
import CardFooter from './CardFooter';

registerIcon(caretDownIcon);

export interface CollapsibleCardProps extends CardProps {
  /** The header content. */
  header?: ReactElement;
  /** Action Buttons that will render in the header. */
  actions?: ReactElement | ReactElement[];
  /** The footer content. */
  footer?: ReactNode;
  /** CardMedia content that will render above the header. */
  media?: ReactElement;
  /** Callback when the Card toggles from collapsed to expanded. */
  onChange?: (event: { collapsed: boolean }) => void;
}

const defaultProps: Partial<CollapsibleCardProps> = {};

const StyledToggleButton = styled.button<{ collapsed: boolean }>(({ theme, collapsed }) => {
  return css`
    transition: transform calc(2 * ${theme.base.animation.speed})
      ${theme.base.animation.timing.ease};
    transform: ${collapsed ? 'rotate(-90deg)' : 'rotate(0)'};
    margin-inline-end: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledToggleButton.defaultProps = defaultThemeProp;

/** @deprecated */
const CollapsibleCard: FunctionComponent<CollapsibleCardProps> = (
  props: PropsWithChildren<CollapsibleCardProps>
) => {
  const { header, actions, footer, media, onChange, children, ...restProps } = props;
  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
    onChange?.({ collapsed: !collapsed });
  };

  return (
    <Card {...restProps}>
      {media && <CardMedia>{media}</CardMedia>}
      <CardHeader actions={actions} onClick={toggleCollapse}>
        <StyledToggleButton
          as={Button}
          variant='simple'
          icon
          onClick={toggleCollapse}
          collapsed={collapsed}
        >
          <Icon name='caret-down' />
        </StyledToggleButton>
        {header}
      </CardHeader>
      <ExpandCollapse collapsed={collapsed}>
        {children && <CardContent>{children}</CardContent>}
        {footer && <CardFooter justify='center'>{footer}</CardFooter>}
      </ExpandCollapse>
    </Card>
  );
};

CollapsibleCard.defaultProps = defaultProps;

export default CollapsibleCard;
